import React, {useState, useRef, useEffect} from 'react';
import {Link} from "react-router-dom";
import {SearchOutlined} from '@ant-design/icons';
import {Button, Input, Space, Table, Tag} from 'antd';
import Highlighter from 'react-highlight-words';
import Title from "../../../components/Title";
import Error from "../../../components/Error";
import {useSelector} from "react-redux";
import {io} from "socket.io-client";

const Index = () => {


    const {user} = useSelector(state => state.auth)
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });

    useEffect(() => {
        setLoading(true);
        const socket = io('https://live.myoctoopidev.com', {
            reconnection: true,
            transports: ['websocket'],
            reconnectionDelay: 2000,
            reconnectionAttempts: 5,
            withCredentials: true,
            query: {
                'x-access-token': user?.token
            },
            transportOptions: {
                binary: true,
            },
        });
        socket.on('map', (data) => {
            setData(data)
            setLoading(false);
        });
        socket.on('reconnect', (event) => {
            console.log('you have been reconnected');
            console.log('Event => ', event);
        });

        socket.on('connect_error', (error) => {
            setError(true)
            console.log(error.name);
        });

        return () => {
            socket.disconnect();
        };
    }, [user?.token]);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };


    const getUniqueCruiseStatusList = () => {
        const cruiseStatusList = data && data.map(record => record.cruiseStatus).filter(Boolean);
        return [...new Set(cruiseStatusList)].map(cruiseStatus => ({text: cruiseStatus, value: cruiseStatus}));
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div style={{padding: 8}}>
                <Input
                    ref={searchInput}
                    placeholder={`Ara ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        Ara
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{width: 90}}
                    >
                        Sıfırla
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filtrele
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Kapat
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1890ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) => {
            const dataIndexValue = record[dataIndex];
            if (dataIndexValue !== null && dataIndexValue !== undefined) {
                if (dataIndex === 'plateNumber') {
                    return dataIndexValue.replace('n', '').toLowerCase().includes(value.toLowerCase());
                }
                return dataIndexValue.toString().toLowerCase().includes(value.toLowerCase());
            }
            return false;
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: 'Device ID',
            dataIndex: 'vehicle_id',
            key: 'vehicle_id',
            width: '11%',
            responsive: ['sm'],
            ...getColumnSearchProps('vehicle_id'),
            sortDirections: ['descend', 'ascend'],
            render: (text, row) => <Link style={{justifyContent: "center"}}
                                         to={`/live/${row.vehicle_id}`}> {text ? text : "-"} </Link>,
        },
        {
            title: 'Plaka',
            dataIndex: 'plateNumber',
            key: 'plateNumber',
            width: '7%',
            sorter: (a, b) => a.plateNumber - b.plateNumber,
            ...getColumnSearchProps('plateNumber'),
        },
        {
            title: 'Kapı No',
            dataIndex: 'operationNumber',
            key: 'operationNumber',
            width: '7%',
            ...getColumnSearchProps('operationNumber'),
        },
        {
            title: 'Son Kayıt Tarih/Saat',
            dataIndex: 'createdAt_last_data',
            key: 'createdAt_last_data',
            width: '10%',
            responsive: ['sm'],
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 6);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' '); // Adjust formatting if needed
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Toplam KM',
            dataIndex: 'highResVehicleTotalDistance',
            key: 'highResVehicleTotalDistance',
            width: '6%',
            responsive: ['sm'],
            sorter: (a, b) => a.highResVehicleTotalDistance - b.highResVehicleTotalDistance,
            render: (text) => (
                <span>
                   {text ? parseInt(text, 10) : '-'}
                </span>
            ),
        },
        {
            title: 'Araç Durumu',
            key: 'cruiseStatus',
            width: '10%',
            responsive: ['lg'],
            dataIndex: ['cruiseStatus'],
            onFilter: (value, record) => {
                const cruiseStatus = record.cruiseStatus ? String(record.cruiseStatus) : '';
                return cruiseStatus === value;
            },
            filters: getUniqueCruiseStatusList(),
            render: (text) => (
                <span style={{textAlign: "center", fontWeight: 'bold'}}>
                    {text}
                </span>
            ),
        },
        {
            title: 'Bağlantı Durumu',
            key: 'onlineStatus',
            width: '10%',
            responsive: ['lg'],
            dataIndex: ['onlineStatus'],
            sorter: (a, b) => a.onlineStatus.localeCompare(b.onlineStatus),
            sortOrder: 'descend',
            onFilter: (value, record) => record.onlineStatus.indexOf(value) === 0,
            filters: [
                {
                    text: 'Online',
                    value: 'online',
                },
                {
                    text: 'Offline',
                    value: 'offline',
                }
            ],
            render: (text) => (
                <span style={{textAlign: "center", fontWeight: 'bold'}}>
                    <Tag color={text === "online" ? "green" : "volcano"}>
                        {text.charAt(0).toUpperCase() + text.slice(1)}
                    </Tag>
                </span>
            ),
        }
    ];

    const onPageChange = (page, pageSize) => {
        setPagination({current: page, pageSize: pageSize});
    };

    if (error) {
        return <Error status={error[0]} title={error[0]} subtitle="Lütfen sonra tekrar deneyin."/>
    }

    return (
        <div style={{padding: 15}}>
            <Title title="Octoopi Cocpit | Canlı Takip" description="Mertech Arge A.Ş Canlı Araç Takip"/>
            <Table
                rowKey="vehicle_id"
                loading={loading}
                bordered={true}
                size="small"
                columns={columns}
                dataSource={data ? data : []}
                error={error}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: onPageChange,
                }}
            />
        </div>
    );

};

export default Index;
